/**
 * Fase D v2 — Canales externos multi-ruta + defaults por evento.
 *
 * Pantalla dividida en 2 secciones:
 *   1. Canales externos — lista de integraciones Slack/Teams.
 *   2. Matriz de eventos — por fila: In-app (tri-state), Email (tri-state),
 *      botón "Externos" con popover multi-select de rutas.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useCierreAlClicAfuera } from '@/lib/useCierreAlClicAfuera'
import { createPortal } from 'react-dom'
import {
  Bell, Mail, Loader2, MessageSquare, Slack, Lock, Trash2, Send,
  ChevronDown, ChevronUp, Plus, ExternalLink, MessageCircle,
} from 'lucide-react'
import { Button } from '@/components/Button'
import { ToastContainer, useToast } from '@/components/Toast'
import { TableSkeleton } from '@/components/Skeleton'
import { StatusBadge } from '@/components/Badge'
import { inputCls } from '@/lib/formStyles'
import {
  useTenantNotificationSettings,
  useUpdateTenantNotificationSettings,
  useExternalChannels,
  useCreateExternalChannel,
  useUpdateExternalChannel,
  useDeleteExternalChannel,
  useTestExternalChannel,
  type NotificationChannel,
  type TenantNotificationSetting,
  type ExternalChannel,
  type ExternalChannelType,
} from '@/modules/notifications/hooks/useNotifications'

const INDIVIDUAL: { key: NotificationChannel; label: string; icon: typeof Bell }[] = [
  { key: 'in_app', label: 'En la app', icon: Bell },
  { key: 'email', label: 'Email', icon: Mail },
]

const MODULE_LABELS: Record<string, string> = {
  'sales.documents': 'Ventas — Documentos',
  'sales.orders': 'Ventas — Pedidos',
  'sales.subscriptions': 'Ventas — Suscripciones',
  'sales.collections': 'Ventas — Cobranza',
  'purchases.invoices': 'Compras — Facturas',
  'receivables.collections': 'Cobranza',
  'receivables.reminders': 'Cobranza — Recordatorios',
  'payables': 'Cuentas por Pagar',
  'inventory': 'Inventario',
  'inventory.transfers': 'Inventario — Transferencias',
  'payroll.payslips': 'Nómina — Liquidaciones',
  'payroll.contracts': 'Nómina — Contratos',
  'config': 'Configuración',
}

const CHANNEL_META: Record<ExternalChannelType, { label: string; icon: typeof Slack; placeholder: string; docsUrl: string }> = {
  slack: {
    label: 'Slack',
    icon: Slack,
    placeholder: 'https://hooks.slack.com/services/…',
    docsUrl: 'https://api.slack.com/messaging/webhooks',
  },
  teams: {
    label: 'Microsoft Teams',
    icon: MessageSquare,
    placeholder: 'https://outlook.office.com/webhook/…',
    docsUrl: 'https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook',
  },
  google_chat: {
    label: 'Google Chat',
    icon: MessageCircle,
    placeholder: 'https://chat.googleapis.com/v1/spaces/…/messages?key=…',
    docsUrl: 'https://developers.google.com/workspace/chat/quickstart/webhooks',
  },
}

type CellState = 'off' | 'default' | 'forced'

type DraftEntry = {
  channels: NotificationChannel[]
  forced_channels: NotificationChannel[]
  external_route_ids: string[]
}

type DraftMap = Record<string, DraftEntry>

function toDraft(items: TenantNotificationSetting[]): DraftMap {
  return Object.fromEntries(
    items.map(i => [
      i.event_type,
      {
        channels: [...i.channels],
        forced_channels: [...i.forced_channels],
        external_route_ids: [...i.external_route_ids],
      },
    ]),
  )
}

function getCellState(entry: DraftEntry, channel: NotificationChannel): CellState {
  if (entry.forced_channels.includes(channel)) return 'forced'
  if (entry.channels.includes(channel)) return 'default'
  return 'off'
}

function cycleState(entry: DraftEntry, channel: NotificationChannel): DraftEntry {
  const state = getCellState(entry, channel)
  if (state === 'off') return { ...entry, channels: [...entry.channels, channel] }
  if (state === 'default') return {
    ...entry,
    channels: entry.channels,
    forced_channels: [...entry.forced_channels, channel],
  }
  return {
    ...entry,
    channels: entry.channels.filter(c => c !== channel),
    forced_channels: entry.forced_channels.filter(c => c !== channel),
  }
}

export function TenantNotificationsTab() {
  const { data, isLoading } = useTenantNotificationSettings()
  const { data: externals = [] } = useExternalChannels()
  const { mutate: update, isPending } = useUpdateTenantNotificationSettings()
  const toast = useToast()
  const [draft, setDraft] = useState<DraftMap>({})

  useEffect(() => {
    if (data) setDraft(toDraft(data))
  }, [data])

  const grouped = useMemo(() => {
    if (!data) return []
    const map = new Map<string, TenantNotificationSetting[]>()
    for (const s of data) {
      const arr = map.get(s.module) || []
      arr.push(s)
      map.set(s.module, arr)
    }
    return Array.from(map.entries()).map(([module, items]) => ({ module, items }))
  }, [data])

  const dirty = useMemo(() => {
    if (!data) return false
    return data.some(s => {
      const d = draft[s.event_type]
      if (!d) return false
      const eqList = (a: string[], b: string[]) =>
        a.length === b.length && a.every(x => b.includes(x))
      return !(
        eqList(d.channels, s.channels) &&
        eqList(d.forced_channels, s.forced_channels) &&
        eqList(d.external_route_ids, s.external_route_ids)
      )
    })
  }, [data, draft])

  const cycle = (eventType: string, channel: NotificationChannel) => {
    setDraft(prev => ({ ...prev, [eventType]: cycleState(prev[eventType], channel) }))
  }

  const setRoutes = (eventType: string, ids: string[]) => {
    setDraft(prev => ({ ...prev, [eventType]: { ...prev[eventType], external_route_ids: ids } }))
  }

  const onSave = () => {
    const payload = Object.entries(draft).map(([event_type, d]) => ({ event_type, ...d }))
    update(payload, {
      onSuccess: () => toast.success('Configuración de la empresa actualizada'),
      onError: () => toast.error('No se pudo actualizar la configuración'),
    })
  }

  const onReset = () => {
    if (data) setDraft(toDraft(data))
  }

  if (isLoading) return <TableSkeleton rows={8} cols={4} />

  return (
    <div data-tour="notif-config-page" className="space-y-6">
      <div data-tour="notif-config-external">
        <ExternalChannelsSection channels={externals} />
      </div>

      <div data-tour="notif-config-forced" className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
        <h3 className="text-sm font-semibold text-content mb-1">Defaults por evento</h3>
        <p className="text-sm text-content-muted">
          Click en <strong>In-app</strong> o <strong>Email</strong> para ciclar:
          <span className="ml-1 inline-flex items-center gap-1 text-content-subtle">Off</span> →{' '}
          <span className="inline-flex items-center gap-1 text-primary">Default</span> →{' '}
          <span className="inline-flex items-center gap-1 text-warning-content">Obligatorio</span>.
          Los eventos con candado no pueden ser desactivados por los usuarios en su perfil.
        </p>
      </div>

      {grouped.map(({ module, items }, idx) => (
        <div key={module} data-tour={idx === 0 ? 'notif-config-matrix' : undefined} className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
          <div className="px-5 py-3 border-b border-divider bg-surface-muted">
            <h3 className="text-sm font-semibold text-content">{MODULE_LABELS[module] || module}</h3>
          </div>
          <div className="divide-y divide-divider">
            {items.map(ev => {
              const entry = draft[ev.event_type] || { channels: [], forced_channels: [], external_route_ids: [] }
              return (
                <div key={ev.event_type} className="flex items-center gap-4 px-5 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-content truncate">{ev.description}</p>
                    <p className="text-xs text-content-subtle">{ev.event_type}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {INDIVIDUAL.map(ch => (
                      <ChannelCell key={ch.key} state={getCellState(entry, ch.key)} label={ch.label}
                                   icon={ch.icon} onClick={() => cycle(ev.event_type, ch.key)} />
                    ))}
                    <div className="w-px h-6 bg-surface-muted mx-1" />
                    <ExternalRoutesPicker
                      channels={externals}
                      selectedIds={entry.external_route_ids}
                      onChange={ids => setRoutes(ev.event_type, ids)}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      <div className="sticky bottom-0 bg-surface border-t border-divider p-4 flex justify-end gap-2 rounded-b-2xl">
        <Button variant="ghost" onClick={onReset} disabled={!dirty || isPending}>
          Descartar
        </Button>
        <Button
          variant="primary"
          onClick={onSave}
          disabled={!dirty || isPending}
          leftIcon={isPending ? <Loader2 size={14} className="animate-spin" /> : undefined}
        >
          Guardar cambios
        </Button>
      </div>

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

// ─── Sección canales externos ────────────────────────────────────────────────

function ExternalChannelsSection({ channels }: { channels: ExternalChannel[] }) {
  const [open, setOpen] = useState(true)

  const byType = useMemo(() => {
    const m: Record<ExternalChannelType, ExternalChannel[]> = { slack: [], teams: [], google_chat: [] }
    for (const c of channels) m[c.channel_type].push(c)
    return m
  }, [channels])

  return (
    <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-hidden shadow-card">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-5 py-3 border-b border-divider bg-surface-muted flex items-center justify-between"
      >
        <div className="text-left">
          <h3 className="text-sm font-semibold text-content">Canales externos</h3>
          <p className="text-xs text-content-muted mt-0.5">
            Integraciones Slack / Microsoft Teams. Cada canal es compartido — todos los
            miembros del canal reciben el mensaje.
          </p>
        </div>
        {open ? <ChevronUp size={18} className="text-content-subtle" /> : <ChevronDown size={18} className="text-content-subtle" />}
      </button>
      {open && (
        <div className="divide-y divide-divider">
          <ChannelGroup channelType="slack" items={byType.slack} />
          <ChannelGroup channelType="teams" items={byType.teams} />
          <ChannelGroup channelType="google_chat" items={byType.google_chat} />
        </div>
      )}
    </div>
  )
}

function ChannelGroup({
  channelType, items,
}: { channelType: ExternalChannelType; items: ExternalChannel[] }) {
  const [adding, setAdding] = useState(false)
  const meta = CHANNEL_META[channelType]
  const Icon = meta.icon

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-surface-muted flex items-center justify-center">
            <Icon size={16} className="text-content-muted" />
          </div>
          <h4 className="text-sm font-semibold text-content">{meta.label}</h4>
          <a href={meta.docsUrl} target="_blank" rel="noreferrer"
             className="text-xs text-primary hover:underline inline-flex items-center gap-1">
            ¿cómo obtengo el webhook? <ExternalLink size={11} />
          </a>
        </div>
        {!adding && (
          <Button variant="ghost" leftIcon={<Plus size={14} />} onClick={() => setAdding(true)}>
            Añadir
          </Button>
        )}
      </div>

      {items.length === 0 && !adding && (
        <p className="text-sm text-content-subtle italic py-2">Ningún canal configurado.</p>
      )}

      <div className="space-y-2">
        {items.map(ch => <ChannelRow key={ch.id} channel={ch} />)}
        {adding && <NewChannelRow channelType={channelType} onDone={() => setAdding(false)} />}
      </div>
    </div>
  )
}

function ChannelRow({ channel }: { channel: ExternalChannel }) {
  const toast = useToast()
  const update = useUpdateExternalChannel()
  const del = useDeleteExternalChannel()
  const test = useTestExternalChannel()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(channel.name)
  const [url, setUrl] = useState(channel.url)

  const onSave = () => {
    if (!name.trim() || !url.trim()) return
    update.mutate({ id: channel.id, name: name.trim(), url: url.trim() }, {
      onSuccess: () => {
        toast.success('Canal actualizado')
        setEditing(false)
      },
      onError: () => toast.error('Error al actualizar'),
    })
  }

  const onToggleActive = () => {
    update.mutate({ id: channel.id, is_active: !channel.is_active }, {
      onError: () => toast.error('Error al cambiar estado'),
    })
  }

  const onDelete = () => {
    if (!confirm(`¿Eliminar "${channel.name}"? Se desconectará de todos los eventos.`)) return
    del.mutate(channel.id, { onSuccess: () => toast.success('Canal eliminado') })
  }

  const onTest = () => {
    test.mutate(channel.id, {
      onSuccess: (r) => {
        if (r.ok) toast.success('Mensaje de prueba enviado')
        else toast.error('Fallo al enviar (ver logs del servidor)')
      },
      onError: () => toast.error('No se pudo probar'),
    })
  }

  if (editing) {
    return (
      <div className="p-3 border border-primary-200 bg-primary-50 rounded-lg space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-2">
          <input aria-label="Nombre del canal" value={name} onChange={e => setName(e.target.value)}
                 placeholder="Nombre (ej. #ventas)" className={inputCls} />
          <input aria-label="URL del webhook" value={url} onChange={e => setUrl(e.target.value)}
                 placeholder="URL del webhook" className={inputCls} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => { setEditing(false); setName(channel.name); setUrl(channel.url) }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onSave} disabled={update.isPending || !name.trim() || !url.trim()}>
            Guardar
          </Button>
        </div>
        <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-glass-3 rounded-lg">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-content truncate">{channel.name}</p>
          <StatusBadge active={channel.is_active} />
        </div>
        <p className="text-xs text-content-subtle font-mono truncate">{channel.url}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" onClick={onToggleActive} disabled={update.isPending}>
          {channel.is_active ? 'Desactivar' : 'Activar'}
        </Button>
        <Button variant="ghost" onClick={() => setEditing(true)}>Editar</Button>
        <Button variant="ghost" leftIcon={<Send size={13} />} onClick={onTest}
                disabled={test.isPending || !channel.is_active}>
          Probar
        </Button>
        <Button variant="ghost-warning" leftIcon={<Trash2 size={13} />} onClick={onDelete} disabled={del.isPending}>
          Eliminar
        </Button>
      </div>
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

function NewChannelRow({
  channelType, onDone,
}: { channelType: ExternalChannelType; onDone: () => void }) {
  const toast = useToast()
  const create = useCreateExternalChannel()
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const meta = CHANNEL_META[channelType]

  const onSave = () => {
    if (!name.trim() || !url.trim()) return
    create.mutate(
      { channel_type: channelType, name: name.trim(), url: url.trim(), is_active: true },
      {
        onSuccess: () => {
          toast.success('Canal creado')
          onDone()
        },
        onError: (e: any) => {
          const details = e?.response?.data?.error?.details
          const msg = details ? Object.values(details).flat().join(' ') : 'Error al crear canal'
          toast.error(msg)
        },
      },
    )
  }

  return (
    <div className="p-3 border border-primary-200 bg-primary-50 rounded-lg space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-2">
        <input aria-label="Nombre del canal" value={name} onChange={e => setName(e.target.value)}
               placeholder='Nombre (ej. "#ventas")' className={inputCls} autoFocus />
        <input aria-label="URL del webhook" value={url} onChange={e => setUrl(e.target.value)}
               placeholder={meta.placeholder} className={inputCls} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>Cancelar</Button>
        <Button variant="primary" onClick={onSave} disabled={create.isPending || !name.trim() || !url.trim()}>
          Crear canal
        </Button>
      </div>
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

// ─── Cell tri-state ──────────────────────────────────────────────────────────

function ChannelCell({
  state, icon: Icon, label, onClick,
}: {
  state: CellState
  icon: typeof Bell
  label: string
  onClick: () => void
}) {
  const base = 'w-9 h-9 rounded-lg flex items-center justify-center transition-colors relative cursor-pointer'
  const cls =
    state === 'off' ? 'bg-surface-muted text-content-subtle hover:bg-soft'
    : state === 'default' ? 'bg-primary-50 text-primary hover:bg-primary-100'
    : 'bg-warning-surface-strong text-warning-content hover:bg-warning-border'
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${cls}`}
      title={`${label} · ${state === 'off' ? 'Off' : state === 'default' ? 'Default' : 'Obligatorio'}`}
      aria-label={`${label} — ${state}`}
    >
      <Icon size={15} />
      {state === 'forced' && (
        <Lock size={9} className="absolute -top-0.5 -right-0.5 text-warning-content" />
      )}
    </button>
  )
}

// ─── Popover de rutas externas ───────────────────────────────────────────────

function ExternalRoutesPicker({
  channels, selectedIds, onChange,
}: {
  channels: ExternalChannel[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  useCierreAlClicAfuera(open, [anchorRef, panelRef], () => setOpen(false))
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const hasSelection = selectedIds.length > 0

  const toggle = (id: string) => {
    const next = selectedSet.has(id)
      ? selectedIds.filter(x => x !== id)
      : [...selectedIds, id]
    onChange(next)
  }

  // Recalcula la posición del portal al abrir, en resize y en scroll.
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return
    const compute = () => {
      const rect = anchorRef.current!.getBoundingClientRect()
      const width = 288 // w-72
      const margin = 8
      // Clamp para que no se salga de la ventana.
      const left = Math.min(
        Math.max(margin, rect.right - width),
        window.innerWidth - width - margin,
      )
      // Si no cabe abajo, muestra arriba del anchor.
      const popoverMaxHeight = 320
      const spaceBelow = window.innerHeight - rect.bottom
      const top = spaceBelow < popoverMaxHeight + margin
        ? Math.max(margin, rect.top - popoverMaxHeight - 4)
        : rect.bottom + 4
      setPos({ top, left, width })
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [open])

  if (channels.length === 0) {
    return (
      <span className="text-xs text-content-subtle italic px-2"
            title="Configura canales arriba para habilitar ruteo externo">
        Sin canales externos
      </span>
    )
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`px-2.5 h-9 rounded-lg flex items-center gap-1.5 text-xs font-medium border transition-colors ${
          hasSelection
            ? 'bg-success-surface border-success-border text-success-content hover:bg-success-surface-strong'
            : 'bg-surface-muted border-divider text-content-muted hover:bg-soft'
        }`}
        aria-label="Canales externos ruteados"
      >
        <ExternalLink size={13} />
        {hasSelection ? `${selectedIds.length} canal${selectedIds.length === 1 ? '' : 'es'}` : 'Ninguno'}
        <ChevronDown size={12} />
      </button>
      {open && pos && createPortal(
        <>
          <div ref={panelRef}
            className="fixed bg-surface rounded-lg shadow-lg border border-divider z-[100]"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            <div className="px-3 py-2 border-b border-divider">
              <p className="text-xs font-semibold text-content-muted">Replicar a</p>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {channels.map(ch => {
                const Icon = CHANNEL_META[ch.channel_type].icon
                const checked = selectedSet.has(ch.id)
                return (
                  <label
                    key={ch.id}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-soft ${
                      !ch.is_active ? 'opacity-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(ch.id)}
                      className="w-4 h-4 rounded border-divider-strong text-primary focus:ring-primary/30"
                    />
                    <Icon size={13} className="text-content-muted shrink-0" />
                    <span className="text-sm text-content truncate flex-1">{ch.name}</span>
                    {!ch.is_active && (
                      <span className="text-xs text-content-subtle">(inactivo)</span>
                    )}
                  </label>
                )
              })}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  )
}
